<script lang="ts" setup>
import SearchInput from '~/components/form-elements/SearchInput.vue'
import BrandsSection from '~/components/sections/BrandsSection.vue'
import Page from '~/enums/page'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import type { BrandData } from '~/types/brands'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]
const digits = ['0—9']
const englishAlphabet = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J',
  'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T',
  'U', 'V', 'W', 'X', 'Y', 'Z'
]
const russianAlphabet = [
  'А', 'Б', 'В', 'Г', 'Д', 'Е', 'Ё', 'Ж', 'З', 'И',
  'Й', 'К', 'Л', 'М', 'Н', 'О', 'П', 'Р', 'С', 'Т',
  'У', 'Ф', 'Х', 'Ц', 'Ч', 'Ш', 'Щ', 'Ъ', 'Ы', 'Ь',
  'Э', 'Ю', 'Я'
]
const title = 'Бренды'

const { $apiHelper } = useNuxtApp()

const { data, status } = await useLazyAsyncData(
  () => Promise.all([
    $apiHelper.brands.getBrands({ 'page[size]': 1000 }),
    $apiHelper.brands.getBrands({ 'filter[hasImages]': 'yes', include: 'images', 'page[size]': 24 })
  ]),
  {
    transform: ([brandsResponse, popularBrandsResponse]) => {
      return {
        brandsData: JsonApiHelper.denormalizeResponse(brandsResponse).data,
        popularBrandsData: JsonApiHelper.denormalizeResponse(popularBrandsResponse).data
      }
    }
  }
)

useHead({
  title: 'Экобренды интернет-магазина iTAB',
  meta: [{
    name: 'description',
    content: 'Известные производители нутрицевтиков и натуральных экологичных товаров для здоровья, молодости и красоты на сайте iTAB.'
  }]
})

const query = ref<string | null>(null)

const brandsData = computed<BrandData[]>(() => data.value?.brandsData ?? [])
const popularBrandsData = computed<BrandData[]>(() => data.value?.popularBrandsData ?? [])

const availableDigits = computed(() => letters.value.includes(digits[0]) ? digits : [])
const availableEnglishLetters = computed(
  () => englishAlphabet.filter((x: string): boolean => letters.value.includes(x))
)
const availableRussianLetters = computed(
  () => russianAlphabet.filter((x: string): boolean => letters.value.includes(x))
)
const filteredBrands = computed(
  () => query.value
    ? brandsData.value.filter(
      (x: BrandData): boolean => x.attributes.name.toLowerCase().includes(query.value.toLowerCase())
    )
    : brandsData.value
)
const groupedBrands = computed(() => {
  const groupedBrands: Record<string, BrandData[]> = {}

  filteredBrands.value.forEach((x: BrandData): void => {
    const firstChar = x.attributes.name.charAt(0).toUpperCase()
    const groupKey = /\d/.test(firstChar) ? digits[0] : firstChar

    if (!groupedBrands[groupKey]) {
      groupedBrands[groupKey] = []
    }

    groupedBrands[groupKey].push(x)
  })

  Object.keys(groupedBrands).forEach((x: string): void => {
    groupedBrands[x].sort((a: BrandData, b: BrandData) => a.attributes.name.localeCompare(b.attributes.name))
  })

  return groupedBrands
})
const letters = computed(() => Object.keys(groupedBrands.value ?? {}))
const letters1 = computed(() => [...availableEnglishLetters.value, ...availableDigits.value])
const letters2 = computed(() => availableRussianLetters.value)
const sortedBrandsData = computed(() => {
  const allLetters = [...englishAlphabet, ...digits, ...russianAlphabet]

  return Object.keys(groupedBrands.value)
    .sort((a: string, b: string): number => allLetters.indexOf(a) - allLetters.indexOf(b))
    .reduce(
      (sorted: Record<string, BrandData[]>, key: string): Record<string, BrandData[]> => {
        sorted[key] = groupedBrands.value[key]

        return sorted
      },
      {} as Record<string, BrandData[]>
    )
})
</script>

<template>
  <div>
    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success'">
      <section class="mb-10">
        <div class="container">
          <Breadcrumbs :current="title" :items="breadcrumbsItems" />

          <div class="content-wrapper">
            <Heading :badge-text="filteredBrands.length" :title="title" />

            <SearchInput v-model="query" placeholder="Поиск брендов" />

            <div v-if="letters1.length" class="-m-2 mt-6 flex flex-wrap">
              <div v-for="x in letters1" :key="x" class="m-2">
                <a :href="`#from-${x}`">{{ x }}</a>
              </div>
            </div>

            <div v-if="letters2.length" class="-m-2 mt-2 flex flex-wrap">
              <div v-for="x in letters2" :key="x" class="m-2">
                <a :href="`#from-${x}`">{{ x }}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandsSection :brands-data="popularBrandsData" class="mb-10" :show-button="false" title="Популярные бренды" />

      <section>
        <div class="container">
          <div class="content-wrapper">
            <div v-if="Object.keys(sortedBrandsData).length" class="-my-6">
              <div v-for="(brandsByLetter, letter) in sortedBrandsData" :key="letter" class="relative my-6">
                <div :id="`from-${letter}`" class="absolute top-[-60px]" />

                <div class="h2 !mb-4">
                  {{ letter }}
                </div>

                <div class="-m-1 flex flex-wrap md:-m-2">
                  <NuxtLink
                    v-for="x in brandsByLetter"
                    :key="x.id"
                    class="w-1/2 p-1 md:w-1/3 md:p-2 lg:w-1/5 xl:w-1/6"
                    :to="helpers.getBrandPath(x)"
                  >
                    {{ x.attributes.name }}
                  </NuxtLink>
                </div>
              </div>
            </div>
            <Notification v-else status="info">
              <template v-if="query">
                Нет брендов по&nbsp;запросу «{{ query }}».
              </template>
              <template v-else>
                Нет брендов.
              </template>
            </Notification>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>
